import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, Loader2, ExternalLink, Save, X, Link as LinkIcon, RefreshCw, Star, ListPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const emptyForm = { external_url: '', source: '', publication_date: '', headline: '' };

const cleanDomain = (url) => {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

/**
 * PressReleaseAdminEditor — used inside the "Press Releases" tab of the
 * Edit Book modal (FEAT-008, simplified to "embed external link only").
 * Admin pastes a URL → the system fetches OG metadata → the public detail
 * page shows a rich link card. No long-form content is ever uploaded.
 */
const PressReleaseAdminEditor = ({ bookId, token, onChange }) => {
  const { toast } = useToast();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // Bulk-paste mode — admin pastes a list of URLs (one per line) and we
  // POST each one sequentially. Headlines/dates are auto-detected per URL.
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [bulkText, setBulkText] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null); // {done, total, errors:[{url, msg}]}

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/books/${bookId}/press-releases`);
      setArticles(Array.isArray(res.data) ? res.data : []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  const onField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (article) => {
    setEditingId(article.id);
    setForm({
      external_url: article.external_url || '',
      source: article.source || '',
      publication_date: article.publication_date || '',
      headline: article.headline || '',
    });
  };

  const handleSave = async () => {
    if (!form.external_url.trim()) {
      toast({
        title: 'URL required',
        description: 'Paste the press release link (e.g. AP News, Yahoo News).',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        external_url: form.external_url.trim(),
        source: form.source.trim() || null,
        publication_date: form.publication_date.trim() || null,
        headline: form.headline.trim() || null,
      };
      if (editingId) {
        await axios.put(`${API}/books/${bookId}/press-releases/${editingId}`, payload, { headers });
        toast({ title: 'Link updated' });
      } else {
        await axios.post(`${API}/books/${bookId}/press-releases`, payload, { headers });
        toast({
          title: 'Link added',
          description: 'Source thumbnail and headline auto-fetched from the page.',
        });
      }
      reset();
      await fetchArticles();
      if (onChange) onChange();
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (article) => {
    const label = article.headline || article.og_title || cleanDomain(article.external_url);
    if (!window.confirm(`Remove this press coverage link?\n\n${label}`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/books/${bookId}/press-releases/${article.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Link removed' });
      if (editingId === article.id) reset();
      await fetchArticles();
      if (onChange) onChange();
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async (article) => {
    setBusy(true);
    try {
      // Re-PUT with the same URL → backend re-fetches OG metadata
      await axios.put(
        `${API}/books/${bookId}/press-releases/${article.id}`,
        { external_url: article.external_url },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Metadata refreshed' });
      await fetchArticles();
      if (onChange) onChange();
    } catch (e) {
      toast({
        title: 'Refresh failed',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleBulkAdd = async () => {
    // Parse one URL per non-empty line, dedupe, basic validation
    const raw = (bulkText || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const urls = [];
    const seen = new Set();
    raw.forEach((u) => {
      let url = u;
      // Strip leading bullets / list markers admin might paste accidentally
      url = url.replace(/^[-*•\d.)\s]+/, '').trim();
      if (!/^https?:\/\//i.test(url)) {
        // Best-effort: prepend https:// if it looks domain-like
        if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}/i.test(url)) url = `https://${url}`;
      }
      if (!seen.has(url) && /^https?:\/\//i.test(url)) {
        seen.add(url);
        urls.push(url);
      }
    });
    if (urls.length === 0) {
      toast({
        title: 'No URLs detected',
        description: 'Paste one link per line, with or without https://',
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    setBulkProgress({ done: 0, total: urls.length, errors: [] });
    const headers = { Authorization: `Bearer ${token}` };
    let added = 0;
    const errs = [];
    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      try {
        // Sequential — keeps load reasonable on the OG-fetcher and gives
        // each row predictable feedback.
        // eslint-disable-next-line no-await-in-loop
        await axios.post(
          `${API}/books/${bookId}/press-releases`,
          { external_url: url },
          { headers },
        );
        added += 1;
      } catch (e) {
        errs.push({ url, msg: e.response?.data?.detail || e.message || 'Failed' });
      }
      setBulkProgress({ done: i + 1, total: urls.length, errors: [...errs] });
    }
    setBusy(false);
    if (added > 0) {
      toast({
        title: `Added ${added} link${added === 1 ? '' : 's'}`,
        description: errs.length ? `${errs.length} skipped — see below.` : 'Source thumbnails and headlines auto-fetched.',
      });
      setBulkText('');
      await fetchArticles();
      if (onChange) onChange();
    } else {
      toast({
        title: 'No links were added',
        description: errs[0]?.msg || 'Please check the URLs.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleFeatured = async (article) => {
    setBusy(true);
    try {
      const next = !article.is_featured;
      await axios.post(
        `${API}/books/${bookId}/press-releases/${article.id}/feature`,
        { is_featured: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: next ? 'Pinned as featured' : 'Featured pin removed',
        description: next
          ? 'This article will appear in the hero strip on the book page.'
          : 'The hero strip will hide until you feature another article.',
      });
      await fetchArticles();
      if (onChange) onChange();
    } catch (e) {
      toast({
        title: 'Could not update featured pin',
        description: e.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="press-release-admin-editor">
      <p className="text-xs text-gray-400">
        Paste a press-release URL (AP News, Yahoo, Google News, publisher pages, etc.). The system
        fetches the source's own thumbnail + headline so users see a rich preview that links straight
        to the original article — nothing is hosted here.
      </p>

      {/* Existing links list */}
      <div>
        <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">
          Saved links ({articles.length})
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 italic">Loading…</p>
        ) : articles.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No links yet — add the first one below.</p>
        ) : (
          <ul className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="press-release-list">
            {articles.map((a) => (
              <li
                key={a.id}
                className={`bg-white/5 border rounded-lg p-2.5 transition-colors ${
                  a.is_featured
                    ? 'border-amber-400/60 bg-amber-500/5'
                    : editingId === a.id
                    ? 'border-fuchsia-500/60'
                    : 'border-white/10'
                }`}
                data-testid={`press-release-row-${a.id}`}
              >
                <div className="flex items-start gap-2.5">
                  {a.og_image && (
                    <img
                      src={a.og_image}
                      alt=""
                      className="w-14 h-14 rounded object-cover flex-shrink-0 bg-zinc-900"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-fuchsia-300 truncate flex items-center gap-1.5 flex-wrap">
                      {a.is_featured && (
                        <span
                          className="inline-flex items-center gap-1 bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                          data-testid={`press-release-featured-badge-${a.id}`}
                        >
                          <Star className="h-2.5 w-2.5 fill-current" /> Featured
                        </span>
                      )}
                      <span className="font-semibold">{a.source || cleanDomain(a.external_url)}</span>
                      {a.publication_date && <span className="text-gray-500"> · {a.publication_date}</span>}
                    </p>
                    <p className="text-sm text-white font-medium line-clamp-2">
                      {a.headline || a.og_title || cleanDomain(a.external_url)}
                    </p>
                    <a
                      href={a.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300 hover:text-fuchsia-200 mt-1 truncate max-w-full"
                      title={a.external_url}
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{a.external_url}</span>
                    </a>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className={`h-7 w-7 ${
                        a.is_featured
                          ? 'text-amber-300 hover:bg-amber-500/10'
                          : 'text-gray-400 hover:bg-amber-500/10 hover:text-amber-300'
                      }`}
                      onClick={() => handleToggleFeatured(a)}
                      disabled={busy}
                      title={a.is_featured ? 'Unpin featured' : 'Pin as featured (only one per book)'}
                      data-testid={`press-release-feature-${a.id}`}
                    >
                      <Star className={`h-3.5 w-3.5 ${a.is_featured ? 'fill-current' : ''}`} />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-gray-400 hover:bg-white/10 hover:text-white"
                      onClick={() => handleRefresh(a)}
                      disabled={busy}
                      title="Re-fetch thumbnail + headline"
                      data-testid={`press-release-refresh-${a.id}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-fuchsia-300 hover:bg-fuchsia-500/10"
                      onClick={() => handleEdit(a)}
                      title="Edit"
                      data-testid={`press-release-edit-${a.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleDelete(a)}
                      disabled={busy}
                      title="Remove link"
                      data-testid={`press-release-delete-${a.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add / edit form */}
      <div
        className={`border rounded-lg p-3 ${editingId ? 'border-fuchsia-500/60 bg-fuchsia-500/5' : 'border-white/10'}`}
        data-testid="press-release-form"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-xs uppercase tracking-wider text-gray-400">
            {editingId ? 'Edit link' : 'Add press-release links'}
          </p>
          {!editingId && (
            <div className="inline-flex bg-white/5 rounded-md p-0.5 border border-white/10" role="tablist">
              <button
                type="button"
                onClick={() => { setMode('single'); setBulkProgress(null); }}
                className={`px-2.5 py-1 text-xs rounded ${mode === 'single' ? 'bg-fuchsia-600 text-white' : 'text-gray-400 hover:text-white'}`}
                data-testid="press-release-mode-single"
                role="tab"
                aria-selected={mode === 'single'}
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => setMode('bulk')}
                className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1 ${mode === 'bulk' ? 'bg-fuchsia-600 text-white' : 'text-gray-400 hover:text-white'}`}
                data-testid="press-release-mode-bulk"
                role="tab"
                aria-selected={mode === 'bulk'}
              >
                <ListPlus className="h-3 w-3" /> Bulk paste
              </button>
            </div>
          )}
        </div>

        {mode === 'bulk' && !editingId ? (
          <>
            <Label className="text-white text-xs mb-1 block flex items-center gap-1">
              <LinkIcon className="h-3 w-3 text-fuchsia-400" /> Paste URLs — one per line
            </Label>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={6}
              placeholder={`https://apnews.com/article/...\nhttps://www.yahoo.com/entertainment/...\nwww.prnewswire.com/news-releases/...`}
              className="w-full bg-white/5 border border-white/10 rounded-md text-white text-sm p-2 font-mono leading-relaxed focus:outline-none focus:border-fuchsia-400/50"
              data-testid="press-release-bulk-textarea"
            />
            <p className="text-xs text-gray-500 mt-1">
              Headlines and thumbnails are auto-fetched from each page. Duplicates are skipped automatically.
            </p>

            {bulkProgress && (
              <div className="mt-3 bg-zinc-900/60 border border-white/10 rounded p-2 text-xs" data-testid="press-release-bulk-progress">
                <div className="flex items-center gap-2 text-gray-300">
                  <Loader2 className={`h-3 w-3 ${bulkProgress.done < bulkProgress.total ? 'animate-spin' : 'opacity-30'}`} />
                  Processing {bulkProgress.done} / {bulkProgress.total}
                </div>
                {bulkProgress.errors?.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-red-300">
                    {bulkProgress.errors.slice(0, 5).map((er, i) => (
                      <li key={i} className="truncate">
                        ✗ <span className="text-red-200/80">{er.url}</span> — <span className="text-red-200/60">{er.msg}</span>
                      </li>
                    ))}
                    {bulkProgress.errors.length > 5 && (
                      <li className="text-red-300/60">…and {bulkProgress.errors.length - 5} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-3">
              <Button
                type="button"
                size="sm"
                onClick={handleBulkAdd}
                disabled={busy || !bulkText.trim()}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="press-release-bulk-add-btn"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ListPlus className="mr-1 h-3.5 w-3.5" /> Add all links</>}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-2">
              <Label className="text-white text-xs mb-1 block flex items-center gap-1">
                <LinkIcon className="h-3 w-3 text-fuchsia-400" /> External URL <span className="text-fuchsia-400">*</span>
              </Label>
              <Input
                type="url"
                value={form.external_url}
                onChange={onField('external_url')}
                placeholder="https://apnews.com/article/..."
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="press-release-url-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <Label className="text-white text-xs mb-1 block">
                  Source <span className="text-gray-500">(optional, auto-detected)</span>
                </Label>
                <Input
                  value={form.source}
                  onChange={onField('source')}
                  placeholder="e.g. AP News"
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="press-release-source-input"
                />
              </div>
              <div>
                <Label className="text-white text-xs mb-1 block">
                  Publication date <span className="text-gray-500">(optional)</span>
                </Label>
                <Input
                  type="date"
                  value={form.publication_date}
                  onChange={onField('publication_date')}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  data-testid="press-release-date-input"
                />
              </div>
            </div>

            <div className="mb-3">
              <Label className="text-white text-xs mb-1 block">
                Headline <span className="text-gray-500">(optional, auto-detected)</span>
              </Label>
              <Input
                value={form.headline}
                onChange={onField('headline')}
                placeholder="Override the article headline (rarely needed)"
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid="press-release-headline-input"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              {editingId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={reset}
                  className="text-gray-300 hover:text-white"
                  data-testid="press-release-cancel-btn"
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Cancel
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={busy}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="press-release-save-btn"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                  editingId ? <><Save className="mr-1 h-3.5 w-3.5" /> Save changes</> : <><Plus className="mr-1 h-3.5 w-3.5" /> Add link</>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PressReleaseAdminEditor;
